import {FeeCollectionPolicy} from '@gatewee/types/recipient-setting';
import {InvoiceStatus} from './invoice';
import {PaymentProvider} from './provider';
import {WithdrawalStatus} from './withdrawal';
import {PaymentStatusReason} from '@gatewee/types/status-reason';
import {AntifraudAnalysis} from '@gatewee/types/antifraud';

export enum PaymentMethod {
  BANK_SLIP = 'bank_slip',
  BITCOIN = 'bitcoin',
  CREDIT_CARD = 'credit_card',
  PIX = 'pix',
  PIX_AUTOMATIC = 'pix_automatic',
  SLIPIX = 'slipix',
}

export interface PaymentData {
  dueDate: string;
  publicUrl?: string;
}

export interface PaymentDataPix extends PaymentData {
  pixKey: string;
  pixQrCode: string;
}

export interface PaymentDataBankSlip extends PaymentData {
  slipUrl: string;
  slipDocumentNumber?: string;
  slipWallet?: string;
  slipBeneficiaryCode?: string;
  slipBankName?: string;
  slipBankCode: string;
  slipDigitableLine: string;
  slipBarCode: string;
  slipInstructions?: string;
}

export interface PaymentProviderPixResponse<TRes, TReq> {
  providerRequest: TReq;
  providerResponse: TRes;
  providerWebhookReq?: unknown[];
  providerTId: string;
  provider: PaymentProvider;
  status: InvoiceStatus | WithdrawalStatus;
  receipt?: PaymentReceipt;
  webhook?: InvoiceWebhook;
  sandbox?: boolean;
}

/** Outbound webhook delivery log (e.g. invoice.* events). */
export interface InvoiceWebhook {
  /** Event name from the API, e.g. `invoice.created`. */
  event?: string;
  retries: number;
  status: number;
  /** Response body as returned by the client endpoint, or raw string from older APIs. */
  response: string | Record<string, unknown>;
  /** JSON string of response headers from the HTTP client call. */
  headers: string;
  sentAt: Date | string;
}

export interface InstallmentOption {
  installmentCount: number;
  perInstallment: number;
  finalAmount: number;
  interestRate: number; // monthly interest in %
  interestType: 'simple' | 'compound';
  hasInterest: boolean;
  paymentMethod: PaymentMethod;
  description?: string;
}
export interface CreditCardPaymentRules {
  maxInstallments: number;
  noInterestUpTo?: number;
  minPerInstallment?: number;
}

export interface GenericPaymentRules {
  interest: InterestPaymentRule;
  fine: FinePaymentRule;
}

export interface InterestPaymentRule {
  type: 'amount' | 'rate';
  value: number; // If type is 'amount', this is the fixed amount of interest. If 'rate', this is the percentage rate (monthly).
  description?: string; // Optional description of the interest rule
}

export interface FinePaymentRule {
  type: 'amount' | 'rate';
  value: number; // If type is 'amount', this is the fixed amount of fine. If 'rate', this is the percentage rate.
  description?: string; // Optional description of the fine rule
}

/** Early-payment discount for bank slip / Slipix (before due date). */
export interface DiscountPaymentRule {
  type: 'amount' | 'rate';
  /** Amount in BRL currency units, or percentage when type is rate. */
  value: number;
  /** ISO date; discount valid if paid on/before this date (must be on/before invoice dueDate). */
  untilDate: string;
  description?: string;
}

export interface BankSlipPaymentRules {
  interest?: InterestPaymentRule;
  fine?: FinePaymentRule;
  discount?: DiscountPaymentRule;
}

export interface PaymentRules {
  bankSlip?: BankSlipPaymentRules;
  creditCard?: CreditCardPaymentRules;
}

export interface SplitPayment {
  recipientId: string | number;
  amount: number;
  type: 'fixed' | 'percentage';
}

export interface PaymentProviderCreditCardMetadata {
  executionDate?: string;
  authorizationCode?: string;
  authorizationNsu?: string;
  tid?: string;
  brand?: string;
  lastFourDigits?: string;
  installments?: number;
  cardToken?: string;
  acquirerName?: string;
  processorMessage?: string;
  processorResponseCode?: string;
}

export interface PaymentProviderResponse<TRes = unknown, TReq = unknown> {
  providerRequest: TReq;
  providerResponse: TRes;
  providerWebhookReq?: unknown[];
  providerErrorMessage?: string;
  providerTId: string;
  fee?: string;
  liquidAmount?: string;
  feeCollectionPolicy?: FeeCollectionPolicy;
  releaseAt?: Date;
  paidAt?: Date;
  enriched?: boolean;
  paymentMethod?: PaymentMethod;
  creditCard?: PaymentProviderCreditCardMetadata;
  receipt?: PaymentReceipt;
  provider: PaymentProvider;
  paymentData?:
    | PaymentData
    | PaymentDataPix
    | PaymentDataBankSlip
    | PaymentDataSlipix;
  status?: InvoiceStatus | WithdrawalStatus;
  statusReason?: PaymentStatusReason;
  webhook?: InvoiceWebhook;
  sandbox?: boolean;
  antifraudAnalysis?: AntifraudAnalysis;
  error?: string;
}

/**
 * Standard payer/receiver payload on {@link PaymentReceipt} (PIX holders, keys, bank refs).
 */
export interface PaymentReceiptParty {
  holderName?: string;
  holderDocument?: string;
  pixKey?: string;
  agency?: string;
  account?: string;
  ispbCode?: string;
  compeCode?: string;
}

export interface PaymentReceipt {
  id: string;
  e2eId?: string;
  /** ISO string or Date from JSON. */
  date: Date | string;
  payer?: PaymentReceiptParty;
  receiver?: PaymentReceiptParty;
  /** PIX-only semantics; ignore for bank-slip bill payment. */
  directTransfer?: boolean;
  /** Bank-slip cash-out digitable line; omit for PIX. */
  digitableLine?: string;
}

export type PaymentDataSlipix = PaymentDataPix & PaymentDataBankSlip;

/** Scope: which payment triggers the rule (MVP: only pixKey). */
export type GlobalPaymentSplitRuleScope = 'pixKey';

export interface GlobalPaymentSplitRule {
  scope: GlobalPaymentSplitRuleScope;
  /** RecipientPixKey.id – rule applies when payment is received on this key */
  pixKeyId: number;
  /** MVP: rate only (0–100). Amount type deferred. */
  type: 'rate';
  /** Percentage 0–100 */
  value: number;
  /** External only in MVP: RecipientBankAccount.id (same or other recipient in domain) */
  bankAccountId: number;
  description?: string;
}

export type GlobalPaymentSplitRulesSetting = GlobalPaymentSplitRule[];
export interface PaymentSplitCalculation {
  recipientId: number;
  amount: number;
}
